import {
  ConfigurationMapEdge,
  ConfigurationMapField,
  ConfigurationMapHook,
  ConfigurationMapRule,
} from '@rossum/api-client/internal';
import { computeConnected } from './computeConnected';

/**
 * Filter config map data to nodes whose ID matches ANY selected node ID,
 * plus their connected dependency chains.
 *
 * Uses the same smart traversal as click-highlighting (`computeConnected`):
 * - Field matched → transitive field→field formula chain + 1-hop hooks/rules
 * - Hook/rule matched → 1-hop field deps/targets + upstream formula chain +
 *   transitive run-after hook chain
 *
 * Edges, rules, and hooks are filtered to only include those where both
 * endpoints / the node itself are in the visible set.
 */
export const filterConfigMapData = (
  fields: ConfigurationMapField[],
  edges: ConfigurationMapEdge[],
  rules: ConfigurationMapRule[],
  hooks: ConfigurationMapHook[],
  selectedNodeIds: string[]
): {
  fields: ConfigurationMapField[];
  edges: ConfigurationMapEdge[];
  rules: ConfigurationMapRule[];
  hooks: ConfigurationMapHook[];
} => {
  if (selectedNodeIds.length === 0) {
    return { fields, edges, rules, hooks };
  }

  const hookIds = new Set(hooks.map(h => h.id));
  const ruleIds = new Set(rules.map(r => r.id));

  // Expand each selected node using the same smart algorithm as highlighting,
  // then union the results.
  const visibleIds = new Set(
    selectedNodeIds.flatMap(id => [
      ...computeConnected(id, edges, hookIds, ruleIds).nodeIds,
    ])
  );

  return {
    fields: fields.filter(f => visibleIds.has(f.id)),
    edges: edges.filter(
      e => visibleIds.has(e.source) && visibleIds.has(e.target)
    ),
    rules: rules.filter(r => visibleIds.has(r.id)),
    hooks: hooks.filter(h => visibleIds.has(h.id)),
  };
};
